import FormResource from "@/module/Form/Resources/FormResource";
import NumberHelper from "@/module/NumberHelper";
import FormItem from "@/module/FormItem";
import SelectedFormItemWithCount from "@/module/SelectedFormItemWithCount";
import ExchangeRateProvider from "@/module/ExchangeRate/ExchangeRateProvider";

export default class FormHelper {
  static defaultCountriesByLanguage: { [key: string]: string } = {
    cs: "CZ",
    sk: "SK",
    en: "GB",
    de: "DE",
    pl: "PL",
    hu: "HU",
    ru: "RU",
    it: "IT",
    pt: "PT",
    fr: "FR",
    vi: "VI",
    zh: "CN",
  };

  static countryPriority: { [key: string]: number } = {
    CZ: 1,
    SK: 2,
    AT: 5,
    BE: 5,
    BG: 5,
    CY: 5,
    DE: 5,
    DK: 5,
    EE: 5,
    ES: 5,
    "ES-CN": 5,
    FI: 5,
    FR: 5,
    GB: 5,
    GR: 5,
    HR: 5,
    HU: 5,
    IE: 5,
    IT: 5,
    LT: 5,
    LU: 5,
    LV: 5,
    MT: 5,
    NL: 5,
    PL: 5,
    PT: 5,
    RO: 5,
    SE: 5,
    SI: 5,
    "*": 6,
  };

  static euCountries: string[] = [
    "AT",
    "BE",
    "BG",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GR",
    "HR",
    "HU",
    "IE",
    "IT",
    "LT",
    "LU",
    "LV",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SE",
    "SI",
    "SK",
  ];

  static periodPriority: { [key: string]: number } = {
    days: 0,
    weeks: 1,
    months: 2,
    years: 3,
  };

  static resolveDefaultCountry(
    defaultCountry: string | null,
    language: string,
  ): string {
    if (defaultCountry !== null) {
      return defaultCountry;
    }

    if (this.defaultCountriesByLanguage.hasOwnProperty(language)) {
      return this.defaultCountriesByLanguage[language];
    }

    return "CZ";
  }

  static filterAllowedCountries(formResource: FormResource): {
    [key: string]: string;
  } {
    const countries = formResource.countries;

    if (formResource.allow_region_world) {
      return countries;
    }

    const allowedCountries: { [key: string]: string } = {};

    if (formResource.allow_region_eu) {
      for (const index in formResource.countries) {
        if (!this.euCountries.includes(index)) {
          continue;
        }

        allowedCountries[index] = formResource.countries[index];
      }

      return allowedCountries;
    }

    if (formResource.allow_region_cz) {
      allowedCountries["CZ"] = countries["CZ"];
    }

    if (formResource.allow_region_sk) {
      allowedCountries["SK"] = countries["SK"];
    }

    return allowedCountries;
  }

  static getRoundAndPrecisionFunction(
    currency: string,
    formResource: FormResource,
  ) {
    let roundFunction = null;
    let roundPrecision = null;
    const currencyCode = currency.toLocaleLowerCase();
    const functionKey = "round_" + currencyCode + "_function";
    const precisionKey = "round_" + currencyCode + "_precision";

    if (formResource.settings.hasOwnProperty("round_function")) {
      roundFunction = formResource.settings.round_function;
    }

    if (formResource.settings.hasOwnProperty("round_precision")) {
      roundPrecision = formResource.settings.round_precision;
    }

    if (formResource.settings.hasOwnProperty(functionKey)) {
      roundFunction = formResource.settings[functionKey];
    }

    if (formResource.settings.hasOwnProperty(precisionKey)) {
      roundPrecision = formResource.settings[precisionKey];
    }

    return [roundFunction, Number(roundPrecision)];
  }

  static calculateRoundItem(
    prices: { [key: string]: number },
    totalPriceIncludingVat: number,
    totalPrice: number,
    disableRepaymentTotalPrice: number,
    disableRepaymentTotalPriceIncVat: number,
    currency: string,
    formResource: FormResource,
    installments: InstallmentItem[],
  ): {
    totalPrice: number;
    totalPriceIncludingVat: number;
    disableRepaymentTotalPrice: number;
    disableRepaymentTotalPriceIncVat: number;
    installments: InstallmentItem[];
  } {
    let roundItemVatRate = 0;
    let maxPrice = 0;

    for (const index in prices) {
      if (!prices.hasOwnProperty(index)) {
        continue;
      }

      const vatRate = Number(index);
      const price = prices[index];

      if (
        price + NumberHelper.EPSILON > maxPrice ||
        (Math.abs(price - maxPrice) < NumberHelper.EPSILON &&
          vatRate > roundItemVatRate)
      ) {
        roundItemVatRate = vatRate;
        maxPrice = price;
      }
    }

    const [roundFunction, roundPrecision] =
      FormHelper.getRoundAndPrecisionFunction(currency, formResource);

    const roundedTotalPriceIncludingVat = NumberHelper.roundWithFunction(
      totalPriceIncludingVat,
      roundFunction,
      roundPrecision,
    );

    const roundItemPriceIncludingVat = NumberHelper.round(
      roundedTotalPriceIncludingVat - totalPriceIncludingVat,
      6,
    );

    totalPrice += NumberHelper.round(
      roundItemPriceIncludingVat *
        NumberHelper.vatCoefficient(roundItemVatRate),
      6,
    );
    totalPriceIncludingVat += roundItemPriceIncludingVat;

    const roundedDisableRepaymentPriceIncVat = NumberHelper.roundWithFunction(
      disableRepaymentTotalPriceIncVat,
      roundFunction,
      roundPrecision,
    );

    const roundItemDisableRepaymentPriceIncVat = NumberHelper.round(
      roundedDisableRepaymentPriceIncVat - disableRepaymentTotalPriceIncVat,
      6,
    );

    disableRepaymentTotalPrice += NumberHelper.round(
      roundItemDisableRepaymentPriceIncVat *
        NumberHelper.vatCoefficient(roundItemVatRate),
      6,
    );

    disableRepaymentTotalPriceIncVat += roundItemDisableRepaymentPriceIncVat;

    totalPrice = NumberHelper.round(totalPrice, 2);
    totalPriceIncludingVat = NumberHelper.round(totalPriceIncludingVat, 2);
    disableRepaymentTotalPrice = NumberHelper.round(
      disableRepaymentTotalPrice,
      2,
    );
    disableRepaymentTotalPriceIncVat = NumberHelper.round(
      disableRepaymentTotalPriceIncVat,
      2,
    );

    for (let i = 0; i < installments.length; i++) {
      const installment = installments[i];

      installment.installmentAmountIncVat = NumberHelper.roundWithFunction(
        installment.installmentAmountIncVat,
        roundFunction,
        roundPrecision,
      );

      installment.installmentAmount = NumberHelper.roundWithFunction(
        installment.installmentAmount,
        roundFunction,
        roundPrecision,
      );
    }

    return {
      totalPrice: totalPrice,
      totalPriceIncludingVat: totalPriceIncludingVat,
      disableRepaymentTotalPrice: disableRepaymentTotalPrice,
      disableRepaymentTotalPriceIncVat: disableRepaymentTotalPriceIncVat,
      installments: installments,
    };
  }

  static getPrices(selectedFromItems: SelectedFormItemWithCount[]): {
    [key: string]: number;
  } {
    const prices: { [key: string]: number } = {};

    for (const selectedFromItem of selectedFromItems) {
      const key = String(selectedFromItem.formItem.currentVat);

      if (!prices.hasOwnProperty(key)) {
        prices[key] = 0;
      }

      prices[key] +=
        (selectedFromItem.formItem.currentDiscountedPriceIncVat ||
          selectedFromItem.formItem.currentPriceIncVat) *
        selectedFromItem.count;
    }

    return prices;
  }

  static async toDomesticCurrency(
    price: number,
    sourceCurrency: string,
    exchangeRateProvider: ExchangeRateProvider,
    allowImported = false,
  ): Promise<number | undefined> {
    let exchangeRate =
      exchangeRateProvider.findBySourceCurrency(sourceCurrency);

    if (exchangeRate === undefined && !allowImported) {
      return undefined;
    } else {
      exchangeRate =
        await exchangeRateProvider.findBySourceCurrencyOrImport(sourceCurrency);

      if (exchangeRate === undefined) {
        return undefined;
      }
    }

    return price * Number(exchangeRate.exchange_rate);
  }

  static toSourceCurrency(
    price: number,
    sourceCurrency: string,
    exchangeRateProvider: ExchangeRateProvider,
  ): number | undefined {
    const exchangeRate =
      exchangeRateProvider.findBySourceCurrency(sourceCurrency);

    if (exchangeRate === undefined) {
      return undefined;
    }

    return price * (1 / Number(exchangeRate.exchange_rate));
  }

  static sortCountries(
    allowedCountries: { [p: string]: string },
    locale: string,
  ): { [p: string]: string } {
    const countries = Object.keys(allowedCountries);

    countries.sort((a: string, b: string) => {
      let priorityA = FormHelper.countryPriority["*"];
      let priorityB = FormHelper.countryPriority["*"];

      if (FormHelper.countryPriority.hasOwnProperty(a)) {
        priorityA = FormHelper.countryPriority[a];
      }

      if (FormHelper.countryPriority.hasOwnProperty(b)) {
        priorityB = FormHelper.countryPriority[b];
      }

      const result = priorityA - priorityB;

      if (result === 0) {
        return allowedCountries[a].localeCompare(allowedCountries[b], locale);
      }

      return result;
    });

    const out: { [p: string]: string } = {};

    countries.forEach((value: string) => {
      out[value] = allowedCountries[value];
    });

    return out;
  }

  static getAllowedPeriods(
    formItems: FormItem[],
    formPeriod: string = "",
  ): Array<string> {
    const allowedPeriods: Array<string> = [];

    if (formPeriod != "") {
      allowedPeriods.push(formPeriod);
    }

    formItems.forEach((item) => {
      if (item.period === null || allowedPeriods.indexOf(item.period) >= 0) {
        return;
      }

      allowedPeriods.push(item.period);
    });

    return allowedPeriods;
  }

  static sortAllowedPeriods(allowedPeriods: Array<string>): Array<string> {
    return allowedPeriods.sort((a: string, b: string): number => {
      const [aCount, aPeriod] = a.split(" ");
      const [bCount, bPeriod] = b.split(" ");

      const aPeriodPriority = FormHelper.periodPriority[aPeriod];
      const bPeriodPriority = FormHelper.periodPriority[bPeriod];

      const periodPriorityDiff = aPeriodPriority - bPeriodPriority;

      if (periodPriorityDiff === 0) {
        return Number(aCount) - Number(bCount);
      }

      return periodPriorityDiff;
    });
  }

  static createFractionMap(repaymentsCount: number): number[] {
    const fraction = 1 / repaymentsCount;
    const fractions: number[] = [];

    for (let i = 0; i < repaymentsCount; i++) {
      fractions.push(
        i === repaymentsCount - 1
          ? +(1 - fractions.reduce((sum, val) => sum + val, 0)).toFixed(3)
          : +fraction.toFixed(3),
      );
    }

    fractions.sort((a, b) => b - a);

    return fractions;
  }

  static calculatePrice(
    price: number,
    priceIncVat: number,
    repaymentCount: number,
    fractions: number[],
    repaymentNumber: number,
  ): number[] {
    if ((priceIncVat * 100) % repaymentCount === 0) {
      return [price / repaymentCount, priceIncVat / repaymentCount];
    }

    return [
      price * fractions[repaymentNumber - 1],
      priceIncVat * fractions[repaymentNumber - 1],
    ];
  }
}
